import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { isChairmanReadOnly } from '../utils/access';

const API      = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/savings';
const MIFL_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/mifl';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const PAYMENT_MODES = [
  { value: '', label: '-- Mode of Payment --' },
  { value: 'cash',      label: 'Cash' },
  { value: 'centenary', label: 'Centenary Bank' },
  { value: 'dfcu',      label: 'DFCU Bank' },
];

// ── PDF HELPERS ──────────────────────────────────────────────────────────────
const addLetterhead = (doc, title, ref, date) => {
  doc.setFillColor(44, 62, 80); doc.rect(0, 0, 210, 28, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
  doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
  doc.setFontSize(11); doc.setFont('helvetica', 'normal');
  doc.text(title, 105, 21, { align: 'center' });
  doc.setTextColor(0, 0, 0); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(`Ref: ${ref}`, 15, 36);
  doc.text(`Date: ${date}`, 195, 36, { align: 'right' });
  doc.setDrawColor(44, 62, 80); doc.setLineWidth(0.5); doc.line(15, 39, 195, 39);
  return 45;
};

const addMemberBlock = (doc, y, s) => {
  doc.setFillColor(240, 240, 240); doc.rect(15, y, 180, 22, 'F');
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
  doc.text('MEMBER DETAILS', 18, y + 6);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  doc.text(`Name: ${s.full_name}`, 18, y + 13);
  doc.text(`Member No: ${s.member_number}`, 18, y + 19);
  doc.text(`Phone: ${s.phone || 'N/A'}`, 110, y + 13);
  doc.text(`Email: ${s.email || 'N/A'}`, 110, y + 19);
  return y + 28;
};

const addFooter = (doc) => {
  const ph = doc.internal.pageSize.height;
  doc.setFillColor(44, 62, 80); doc.rect(0, ph - 15, 210, 15, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text('Kigezi Association — Official Document — Confidential', 105, ph - 6, { align: 'center' });
};

const generateSavingsReceipt = (s, cycleName, monthNum) => {
  const doc = new jsPDF();
  const payDate  = s.last_payment_date?.slice(0, 10) || new Date().toISOString().split('T')[0];
  const monthLabel = MONTH_NAMES[(monthNum || 1) - 1];
  const ref      = `SAV-${s.member_number}-${cycleName?.replace(/\s+/g,'-')}-M${monthNum}`;
  let y = addLetterhead(doc, 'OFFICIAL RECEIPT — MONTHLY SAVINGS', ref, payDate);
  y = addMemberBlock(doc, y, s);
  autoTable(doc, {
    startY: y,
    head: [['Field', 'Details']],
    body: [
      ['Financial Cycle', cycleName || '—'],
      ['Contribution Month', `${monthLabel}`],
      ['Monthly Rate (UGX)', Number(s.monthly_amount || 0).toLocaleString()],
      ['Amount Paid (UGX)', Number(s.amount_paid || 0).toLocaleString()],
      ['Balance Outstanding (UGX)', Number(s.balance || 0).toLocaleString()],
      ['Payment Date', payDate],
      ['Status', (s.status || '').toUpperCase()],
    ],
    styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 10;
  const isFullyPaid = s.status === 'paid' || parseFloat(s.balance || 0) === 0;
  doc.setFillColor(isFullyPaid ? 39 : 243, isFullyPaid ? 174 : 156, isFullyPaid ? 96 : 18);
  doc.rect(15, y, 180, 14, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(isFullyPaid ? 'SAVINGS FULLY PAID — THANK YOU' : 'PARTIAL PAYMENT RECEIVED — BALANCE REMAINS', 105, y + 9, { align: 'center' });
  doc.setTextColor(0, 0, 0); y += 22;
  if (s.signature_snapshot) {
    try {
      const fmt = s.signature_snapshot.includes('data:image/png') ? 'PNG' : 'JPEG';
      doc.addImage(s.signature_snapshot, fmt, 15, y - 8, 40, 10);
    } catch (e) {}
  }
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('This receipt confirms the monthly savings payment recorded above.', 15, y + 6, { maxWidth: 180 });
  y += 18;
  doc.setDrawColor(100, 100, 100);
  doc.line(15, y, 90, y); doc.line(115, y, 195, y);
  doc.text('Received By (Finance Committee)', 15, y + 5);
  doc.text(s.officer_name || 'Finance Committee', 15, y + 11);
  doc.text('Member Signature', 115, y + 5);
  doc.text('Date: ___________', 115, y + 11);
  addFooter(doc);
  doc.save(`Savings_Receipt_${s.member_number}_${cycleName}_M${monthNum}.pdf`);
};

// ── COMPONENT ─────────────────────────────────────────────
function Savings() {
  const [cycles, setCycles]           = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [selectedMonth, setSelectedMonth]     = useState(new Date().getMonth() + 1);
  const [summary, setSummary]         = useState({ monthly_amount: 0, members: [], cycle: null });
  const [rates, setRates]             = useState([]);
  const [showRateForm, setShowRateForm]       = useState(false);
  const [showPayForm, setShowPayForm]         = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  // Inline quick-pay
  const [quickPayId, setQuickPayId]   = useState(null);
  const [quickPayData, setQuickPayData] = useState({ amount_paid: '', mode_of_payment: '', receipt_ref: '' });

  // Mark as Missed
  const [markMissedId, setMarkMissedId]   = useState(null);
  const [markingMissed, setMarkingMissed] = useState(false);

  // Edit
  const [editingId, setEditingId]     = useState(null);
  const [editData, setEditData]       = useState({});

  const [rateForm, setRateForm] = useState({ month: '', cycle_id: '', amount: '' });

  const [payForm, setPayForm] = useState({
    member_id: '', month: '', cycle_id: '', amount_paid: '',
    mode_of_payment: '', receipt_ref: ''
  });
  const readOnly = isChairmanReadOnly();

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Load cycles on mount
  useEffect(() => {
    axios.get(`${API}/cycles`, { headers }).then(res => {
      setCycles(res.data);
      // Default to first active cycle
      const active = res.data.find(c => c.status === 'active');
      if (active) setSelectedCycleId(String(active.id));
    }).catch(() => {});
    fetchRates();
  }, []);

  // Fetch summary when cycle or month changes
  useEffect(() => {
    if (selectedCycleId && selectedMonth) fetchSummary();
  }, [selectedCycleId, selectedMonth]);

  const fetchRates = () => {
    axios.get(`${API}/rates`, { headers }).then(res => setRates(res.data)).catch(() => {});
  };

  const fetchSummary = () => {
    if (!selectedCycleId || !selectedMonth) return;
    axios.get(`${API}/summary/${selectedCycleId}/${selectedMonth}`, { headers })
      .then(res => setSummary(res.data))
      .catch(err => showMsg(err.response?.data?.error || 'Failed to load summary', true));
  };

  const showMsg = (text, isError = false) => {
    if (isError) { setError(text); setSuccess(''); } else { setSuccess(text); setError(''); }
  };

  const handleRateSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/rates`, rateForm, { headers });
      showMsg('Rate saved');
      setShowRateForm(false);
      setRateForm({ month: '', cycle_id: '', amount: '' });
      fetchRates(); fetchSummary();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to save rate', true); }
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/pay`, { ...payForm, cycle_id: selectedCycleId, month: selectedMonth }, { headers });
      showMsg('Payment recorded');
      setShowPayForm(false);
      setPayForm({ member_id: '', month: '', cycle_id: '', amount_paid: '', mode_of_payment: '', receipt_ref: '' });
      fetchSummary();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to record payment', true); }
  };

  const openQuickPay = (s) => {
    setQuickPayId(s.member_id);
    setQuickPayData({ amount_paid: s.balance, mode_of_payment: '', receipt_ref: '' });
    setMarkMissedId(null); setEditingId(null);
  };

  const submitQuickPay = async (s) => {
    if (!quickPayData.amount_paid) return showMsg('Enter amount', true);
    try {
      await axios.post(`${API}/pay`, {
        member_id:       s.member_id,
        month:           selectedMonth,
        cycle_id:        selectedCycleId,
        amount_paid:     quickPayData.amount_paid,
        mode_of_payment: quickPayData.mode_of_payment || undefined,
        receipt_ref:     quickPayData.receipt_ref     || undefined,
      }, { headers });
      showMsg('Payment recorded');
      setQuickPayId(null); fetchSummary();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', true); }
  };

  const startEdit = (s) => {
    setEditingId(s.member_id);
    setEditData({ amount_paid: s.amount_paid, status: s.status, mode_of_payment: '', receipt_ref: '' });
    setQuickPayId(null); setMarkMissedId(null);
  };

  const saveEdit = async (s) => {
    try {
      await axios.patch(`${API}/${s.member_id}/${selectedCycleId}/${selectedMonth}`, editData, { headers });
      showMsg('Record updated'); setEditingId(null); fetchSummary();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to update', true); }
  };

  const confirmMarkMissed = (s) => {
    setMarkMissedId(s.member_id); setQuickPayId(null); setEditingId(null);
  };

  const submitMarkMissed = async (s) => {
    if (!summary.monthly_amount) return showMsg('No rate set — cannot post MDN', true);
    setMarkingMissed(true);
    try {
      const res = await axios.post(`${MIFL_API}/mark-missed`, {
        member_id:       s.member_id,
        obligation_type: 'savings',
        amount:          summary.monthly_amount,
        description:     `Missed Monthly Savings — ${MONTH_NAMES[(selectedMonth || 1) - 1]} (${summary.cycle?.cycle_name || ''})`,
        entry_date:      new Date().toISOString().split('T')[0],
      }, { headers });
      showMsg(`MDN posted: ${res.data.document_number}`);
      setMarkMissedId(null); fetchSummary();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', true); }
    finally { setMarkingMissed(false); }
  };

  const selectedCycle = cycles.find(c => String(c.id) === String(selectedCycleId));
  const rateForCycleMonth = rates.find(r => String(r.cycle_id) === String(selectedCycleId) && String(r.month) === String(selectedMonth));

  const fullyPaid  = summary.members.filter(m => m.status === 'paid').length;
  const partial    = summary.members.filter(m => m.status === 'partial').length;
  const unpaid     = summary.members.filter(m => m.status === 'unpaid' || m.status === 'no_rate').length;
  const totalCollected = summary.members.reduce((s, m) => s + parseFloat(m.amount_paid || 0), 0);
  const totalExpected  = (summary.monthly_amount || 0) * summary.members.length;

  return (
    <div style={{ padding: '20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: '#2c3e50', margin: 0 }}>Monthly Savings</h2>
          <p style={{ color: '#7f8c8d', margin: '4px 0 0', fontSize: '13px' }}>
            Reserve Fund savings — UGX {rateForCycleMonth ? Number(rateForCycleMonth.amount).toLocaleString() : '—'} per member per month (AGM-set rate)
            {selectedCycle && <span style={{ marginLeft: '10px', fontWeight: '600', color: '#2c3e50' }}>Cycle: {selectedCycle.cycle_name}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!readOnly && <button onClick={() => { setShowRateForm(!showRateForm); setShowPayForm(false); }} style={btnStyle}>{showRateForm ? 'Cancel' : '⚙ Set Rate'}</button>}
          {!readOnly && <button onClick={() => { setShowPayForm(!showPayForm); setShowRateForm(false); }} style={{ ...btnStyle, background: '#27ae60' }}>{showPayForm ? 'Cancel' : '+ Record Payment'}</button>}
        </div>
      </div>

      {error   && <div style={alertStyle('#e74c3c')}>{error}<span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {success && <div style={alertStyle('#2ecc71')}>{success}<span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      {/* Set Rate Form */}
      {!readOnly && showRateForm && (
        <form onSubmit={handleRateSubmit} style={formStyle}>
          <h3 style={{ margin: '0 0 15px', color: '#2c3e50', width: '100%' }}>Set AGM Savings Rate</h3>
          <div style={fieldStyle}>
            <label style={labelStyle}>Financial Cycle *</label>
            <select style={inputStyle} value={rateForm.cycle_id} onChange={e => setRateForm({ ...rateForm, cycle_id: e.target.value })} required>
              <option value=''>-- Select Cycle --</option>
              {cycles.map(c => <option key={c.id} value={c.id}>{c.cycle_name}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Month *</label>
            <select style={inputStyle} value={rateForm.month} onChange={e => setRateForm({ ...rateForm, month: e.target.value })} required>
              <option value=''>-- Select Month --</option>
              {MONTH_NAMES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Amount (UGX) *</label>
            <input type="number" style={inputStyle} value={rateForm.amount} onChange={e => setRateForm({ ...rateForm, amount: e.target.value })} required />
          </div>
          <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
            <button type="submit" style={btnStyle}>Save Rate</button>
            <button type="button" onClick={() => setShowRateForm(false)} style={{ ...btnStyle, background: '#95a5a6' }}>Cancel</button>
          </div>
        </form>
      )}

      {/* Record Payment Form */}
      {!readOnly && showPayForm && (
        <form onSubmit={handlePaySubmit} style={formStyle}>
          <h3 style={{ margin: '0 0 15px', color: '#2c3e50', width: '100%' }}>Record Payment</h3>
          <div style={fieldStyle}>
            <label style={labelStyle}>Member *</label>
            <select style={inputStyle} value={payForm.member_id} onChange={e => setPayForm({ ...payForm, member_id: e.target.value })} required>
              <option value=''>-- Select Member --</option>
              {summary.members.map(m => <option key={m.member_id} value={m.member_id}>{m.full_name} ({m.member_number})</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Amount Paid (UGX) *</label>
            <input type="number" style={inputStyle} value={payForm.amount_paid} onChange={e => setPayForm({ ...payForm, amount_paid: e.target.value })} required />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Mode of Payment</label>
            <select style={inputStyle} value={payForm.mode_of_payment} onChange={e => setPayForm({ ...payForm, mode_of_payment: e.target.value })}>
              {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Receipt Ref</label>
            <input style={inputStyle} value={payForm.receipt_ref} onChange={e => setPayForm({ ...payForm, receipt_ref: e.target.value })} placeholder='Optional' />
          </div>
          <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
            <button type="submit" style={{ ...btnStyle, background: '#27ae60' }}>Record Payment</button>
            <button type="button" onClick={() => setShowPayForm(false)} style={{ ...btnStyle, background: '#95a5a6' }}>Cancel</button>
          </div>
        </form>
      )}

      {/* Cycle + Month selectors */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', margin: '20px 0 16px', flexWrap: 'wrap', background: '#f9f9f9', padding: '12px 16px', borderRadius: '8px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#7f8c8d', marginBottom: '4px' }}>Financial Cycle</label>
          <select style={{ ...inputStyle, minWidth: '160px' }} value={selectedCycleId} onChange={e => setSelectedCycleId(e.target.value)}>
            <option value=''>-- Select Cycle --</option>
            {cycles.map(c => <option key={c.id} value={c.id}>{c.cycle_name} ({c.status})</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#7f8c8d', marginBottom: '4px' }}>Month</label>
          <select style={{ ...inputStyle, minWidth: '140px' }} value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
            {MONTH_NAMES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
        {selectedCycle && (
          <div style={{ fontSize: '13px', color: '#2c3e50', alignSelf: 'center' }}>
            <strong>{MONTH_NAMES[(selectedMonth || 1) - 1]}</strong> — Cycle <strong>{selectedCycle.cycle_name}</strong>
            {rateForCycleMonth
              ? <span style={{ marginLeft: '10px', color: '#27ae60', fontWeight: '600' }}>Rate: UGX {Number(rateForCycleMonth.amount).toLocaleString()}</span>
              : <span style={{ marginLeft: '10px', color: '#e74c3c' }}>⚠ No rate set</span>
            }
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={kpiCard('#2ecc71')}><div style={kpiLabel}>Fully Paid</div><div style={kpiValue}>{fullyPaid}</div></div>
        <div style={kpiCard('#f39c12')}><div style={kpiLabel}>Partial</div><div style={kpiValue}>{partial}</div></div>
        <div style={kpiCard('#e74c3c')}><div style={kpiLabel}>Unpaid</div><div style={kpiValue}>{unpaid}</div></div>
        <div style={kpiCard('#2c3e50')}>
          <div style={kpiLabel}>Collected / Expected</div>
          <div style={{ ...kpiValue, fontSize: '14px' }}>UGX {totalCollected.toLocaleString()} / {totalExpected.toLocaleString()}</div>
        </div>
      </div>

      {/* Members Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#2c3e50', color: 'white' }}>
              <th style={thStyle}>Member</th>
              <th style={thStyle}>Monthly Due</th>
              <th style={thStyle}>Paid (UGX)</th>
              <th style={thStyle}>Balance (UGX)</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Last Payment</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {summary.members.length === 0 && (
              <tr><td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>
                {selectedCycleId ? 'No records — set a rate to begin' : 'Select a cycle to view members'}
              </td></tr>
            )}
            {summary.members.map((s, i) => {

              // EDITING ROW
              if (editingId === s.member_id) return (
                <tr key={s.member_id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                  <td style={tdStyle}><strong>{s.full_name}</strong><br /><span style={{ fontSize: '11px', color: '#95a5a6' }}>{s.member_number}</span></td>
                  <td style={tdStyle}>{summary.monthly_amount ? Number(summary.monthly_amount).toLocaleString() : '—'}</td>
                  <td style={tdStyle}><input type="number" value={editData.amount_paid} onChange={e => setEditData({ ...editData, amount_paid: e.target.value })} style={inlineInput} /></td>
                  <td style={tdStyle}>{summary.monthly_amount ? Math.max(0, parseFloat(summary.monthly_amount) - parseFloat(editData.amount_paid || 0)).toLocaleString() : '—'}</td>
                  <td style={tdStyle}>
                    <select value={editData.status} onChange={e => setEditData({ ...editData, status: e.target.value })} style={inlineInput}>
                      <option value="unpaid">Unpaid</option><option value="partial">Partial</option><option value="paid">Paid</option>
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <select style={inlineInput} value={editData.mode_of_payment} onChange={e => setEditData({ ...editData, mode_of_payment: e.target.value })}>
                        {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                      <input style={inlineInput} placeholder='Receipt ref' value={editData.receipt_ref || ''} onChange={e => setEditData({ ...editData, receipt_ref: e.target.value })} />
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {!readOnly && <button onClick={() => saveEdit(s)} style={{ ...smBtn, background: '#2ecc71', marginRight: '4px' }}>Save</button>}
                    <button onClick={() => setEditingId(null)} style={{ ...smBtn, background: '#95a5a6' }}>Cancel</button>
                  </td>
                </tr>
              );

              // QUICK PAY ROW
              if (quickPayId === s.member_id) return (
                <tr key={s.member_id} style={{ background: '#eafaf1', borderLeft: '4px solid #27ae60' }}>
                  <td style={tdStyle} colSpan={2}><strong style={{ color: '#1e8449' }}>Pay — {s.full_name}</strong></td>
                  <td style={tdStyle}>
                    <input type="number" style={inlineInput} value={quickPayData.amount_paid}
                      onChange={e => setQuickPayData({ ...quickPayData, amount_paid: e.target.value })} />
                  </td>
                  <td style={tdStyle}>
                    <select style={inlineInput} value={quickPayData.mode_of_payment}
                      onChange={e => setQuickPayData({ ...quickPayData, mode_of_payment: e.target.value })}>
                      {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <input style={inlineInput} placeholder='Receipt ref' value={quickPayData.receipt_ref}
                      onChange={e => setQuickPayData({ ...quickPayData, receipt_ref: e.target.value })} />
                  </td>
                  <td style={tdStyle}>{s.last_payment_date ? s.last_payment_date.slice(0, 10) : '—'}</td>
                  <td style={tdStyle}>
                    {!readOnly && <button onClick={() => submitQuickPay(s)} style={{ ...smBtn, background: '#27ae60', marginRight: '4px' }}>Record</button>}
                    <button onClick={() => setQuickPayId(null)} style={{ ...smBtn, background: '#95a5a6' }}>Cancel</button>
                  </td>
                </tr>
              );

              // MARK MISSED CONFIRMATION ROW
              if (markMissedId === s.member_id) return (
                <tr key={s.member_id} style={{ background: '#fdf3f3', borderLeft: '4px solid #e74c3c' }}>
                  <td style={tdStyle} colSpan={6}>
                    <strong style={{ color: '#c0392b' }}>Post MDN for {s.full_name} — {MONTH_NAMES[(selectedMonth || 1) - 1]}?</strong>
                    <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                      UGX {Number(summary.monthly_amount).toLocaleString()} will be debited in MIFL.
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {!readOnly && <button onClick={() => submitMarkMissed(s)} disabled={markingMissed}
                      style={{ ...smBtn, background: '#c0392b', marginRight: '4px' }}>
                      {markingMissed ? 'Posting…' : 'Confirm MDN'}
                    </button>}
                    <button onClick={() => setMarkMissedId(null)} style={{ ...smBtn, background: '#95a5a6' }}>Cancel</button>
                  </td>
                </tr>
              );

              // NORMAL ROW
              const statusColors = { paid: '#2ecc71', partial: '#f39c12', unpaid: '#e74c3c', no_rate: '#95a5a6' };
              return (
                <tr key={s.member_id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                  <td style={tdStyle}>
                    <strong>{s.full_name}</strong><br />
                    <span style={{ fontSize: '11px', color: '#95a5a6' }}>{s.member_number}</span>
                  </td>
                  <td style={tdStyle}>{summary.monthly_amount ? Number(summary.monthly_amount).toLocaleString() : '—'}</td>
                  <td style={{ ...tdStyle, color: '#27ae60', fontWeight: '600' }}>{Number(s.amount_paid).toLocaleString()}</td>
                  <td style={{ ...tdStyle, color: s.balance > 0 ? '#e74c3c' : '#2ecc71', fontWeight: '600' }}>{Number(s.balance).toLocaleString()}</td>
                  <td style={tdStyle}>
                    <span style={{ background: statusColors[s.status] || '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                      {s.status?.toUpperCase()}
                    </span>
                  </td>
                  <td style={tdStyle}>{s.last_payment_date ? s.last_payment_date.slice(0, 10) : '—'}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {s.amount_paid > 0 && (
                        <button onClick={() => generateSavingsReceipt(s, selectedCycle?.cycle_name, selectedMonth)}
                          style={{ background: '#27ae60', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                          📄 Receipt
                        </button>
                      )}
                      {!readOnly && s.status !== 'paid' && summary.monthly_amount > 0 && (
                        <button onClick={() => openQuickPay(s)}
                          style={{ background: '#eaf4fb', color: '#1a5276', border: '1px solid #aed6f1', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                          Pay
                        </button>
                      )}
                      {!readOnly && <button onClick={() => startEdit(s)} style={smBtn}>Edit</button>}
                      {!readOnly && s.status === 'unpaid' && summary.monthly_amount > 0 && (
                        <button onClick={() => confirmMarkMissed(s)}
                          style={{ background: '#c0392b', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                          ⚑ Missed
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Rates Table */}
      {rates.length > 0 && (
        <>
          <h3 style={{ color: '#2c3e50', marginTop: '30px' }}>AGM Rates by Cycle</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  <th style={thStyle}>Cycle</th>
                  <th style={thStyle}>Month</th>
                  <th style={thStyle}>Amount (UGX)</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r, i) => (
                  <tr key={r.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={tdStyle}><strong>{r.cycle_name}</strong></td>
                    <td style={tdStyle}>{MONTH_NAMES[(r.month || 1) - 1]}</td>
                    <td style={tdStyle}>{Number(r.amount).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const btnStyle   = { background: '#2c3e50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' };
const smBtn      = { background: '#2c3e50', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' };
const formStyle  = { background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', border: '1px solid #ecf0f1' };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px' };
const labelStyle = { fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold' };
const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' };
const inlineInput = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #f39c12', fontSize: '12px', background: '#fffde7', width: '100%' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle    = { padding: '10px', textAlign: 'left', fontSize: '13px' };
const tdStyle    = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };
const alertStyle = (color) => ({ background: color, color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' });
const kpiCard    = (color) => ({ background: color, color: 'white', padding: '15px 20px', borderRadius: '8px', minWidth: '140px' });
const kpiLabel   = { fontSize: '11px', opacity: 0.85, marginBottom: '4px' };
const kpiValue   = { fontSize: '24px', fontWeight: 'bold' };

export default Savings;